<template>
  <div class="users-upload">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <v-btn
                icon
                color="primary"
                @click="$router.back()"
                class="back-btn mr-3"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-upload</v-icon>
                Upload Users
                <span class="page-inline-subtitle">Bulk upload users via Excel file</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  text
                  :href="`${mediaUrl}`"
                  target="_blank"
                  class="modern-btn mr-2"
                  elevation="2"
                >
                  <v-icon left>mdi-download</v-icon>
                  Download Sample
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Section -->
      <v-row>
        <v-col cols="12">
          <v-card class="upload-card" elevation="6">
            <v-card-title class="modern-card-title">
              <h2 class="table-title">
                <v-icon class="mr-2">mdi-cloud-upload</v-icon>
                File Upload
              </h2>
            </v-card-title>
            <v-card-text class="pa-6">
              <div v-if="crud_users.length == 0" class="upload-area">
                <div class="file-upload-container">
                  <input
                    type="file"
                    id="file"
                    name="file"
                    ref="file"
                    accept=".xlsx,.xls"
                    @change="handleFileUpload"
                    class="file-input"
                  />
                  <label for="file" class="file-upload-label">
                    <v-icon large class="upload-icon">mdi-cloud-upload-outline</v-icon>
                    <div class="upload-text">
                      <h3>Choose Excel File</h3>
                      <p>Click to browse or drag and drop your Excel file here</p>
                    </div>
                  </label>
                </div>
                
                <v-progress-linear
                  v-if="isUploading"
                  indeterminate
                  color="primary"
                  class="mt-4"
                ></v-progress-linear>
                
                <v-alert
                  v-if="isError"
                  type="error"
                  class="mt-4"
                >
                  {{ error_msg }}
                </v-alert>
              </div>
              
              <div v-else class="upload-actions">
                <div class="actions-info">
                  <v-icon large color="success" class="mr-3">mdi-check-circle</v-icon>
                  <div>
                    <h3>File uploaded successfully!</h3>
                    <p>{{ crud_users.length }} users ready to process</p>
                  </div>
                </div>
                <div class="action-buttons">
                  <v-btn
                    color="success"
                    :loading="isProcessing"
                    :disabled="isProcessing"
                    @click="processFile"
                    class="modern-btn mr-3"
                    elevation="2"
                  >
                    <v-icon left>mdi-check</v-icon>
                    Process File
                  </v-btn>
                  <v-btn
                    color="error"
                    @click="truncatePendingDocuments"
                    class="modern-btn"
                    elevation="2"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Delete
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Preview Table -->
      <v-row v-if="crud_users.length > 0">
        <v-col cols="12">
          <v-card class="users-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      Preview Data
                    </h2>
                    <div class="results-count" v-if="crud_users.length > 0">
                      <div class="count-display">
                        <span class="count-number">{{ crud_users.length }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="crud_users"
                :loading="isLoading"
                class="modern-data-table"
                hide-default-footer
                disable-pagination
              >
                <template v-slot:[`item.sr_no`]="{ index }">
                  <span class="serial-number">{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.name`]="{ item }">
                  <div class="user-info">
                    <div class="user-name">{{ item.first_name }} {{ item.middle_name }} {{ item.last_name }}</div>
                  </div>
                </template>

                <template v-slot:[`item.contact`]="{ item }">
                  <div class="contact-info">
                    <div class="contact-email">{{ item.email }}</div>
                    <div class="contact-phone" v-if="item.phone">{{ item.phone }}</div>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Upload Success Dialog Box -->
      <v-dialog v-model="is_Uploaded" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-cloud-upload-outline
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>
                        The file has been uploaded successfully. Now you can
                        Preview the List & process accordingly.
                      </h3>
                      <br />
                      <v-btn color="success" @click="is_Uploaded = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      
      <!-- Process Success Dialog Box -->
      <v-dialog v-model="is_Processed" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-progress-download
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>
                        The file has been processed successfully. Now you can
                        see your users in User's Tab.
                      </h3>
                      <br />
                      <v-btn color="success" @click="is_Processed = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      
      <!-- Truncate Success Dialog Box -->
      <v-dialog v-model="is_Truncated" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: red; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty-outline
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Deleted!</h1>
                      <br />
                      <h3>
                        The file has been deleted successfully. Now you can
                        select a new file to upload.
                      </h3>
                      <br />
                      <v-btn color="success" @click="is_Truncated = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";
// import backbutton from "@/components/backbutton.vue";
export default {
  //   components: { backbutton },
  metaInfo: {
    // if no subcomponents specify a metaInfo.title, this title will be used
    title: "Upload User Excel",
  },
  data() {
    return {
      crud_users: [],
      isLoading: false,
      isUploading: false,
      is_Uploaded: false,
      isProcessing: false,
      is_Processed: false,
      is_Truncated: false,
      isError: false,
      error_msg: "",
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'User', value: 'name', sortable: false, width: '250px' },
        { text: 'Contact', value: 'contact', sortable: false, width: '220px' },
        { text: 'Position', value: 'position', sortable: false, width: '180px' },
        { text: 'Upline User', value: 'upline_user', sortable: false, width: '180px' },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    async getData(page = 1) {
      this.isLoading = true;
      try {
        // TODO: Implement crud_users endpoint
        // await axios
        //   .get(`/crud_users`)
        //   .then((crud_users) => {
        //     this.crud_users = crud_users.data.data;
        //     this.count = crud_users.data.count;
        //     this.serialNoStarting = (page - 1) * this.rowsPerPage;
        //     this.isLoading = false;
        //   })
        //   .catch(function (error) {
        //     console.log(error);
        //     window.location.href = "/auth/login";
        //   });
        console.log("CRUD users endpoint not implemented");
        this.isLoading = false;
      } catch (error) {
        console.log(error);
      }
    },
    async handleFileUpload() {
      const file = this.$refs.file.files[0];
      if (!file) return;

      this.isUploading = true;
      this.isError = false;
      this.error_msg = "";
      
      try {
        const formData = new FormData();
        formData.append("users", file);
        
        const response = await axios.post("crud_users/upload_user", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        
        if (response.data.data) {
          this.crud_users = response.data.data;
          this.is_Uploaded = true;
        }
      } catch (error) {
        this.isError = true;
        this.error_msg = "Error uploading file. Please check the file format and try again.";
        console.error("Upload error:", error);
      }
      
      this.isUploading = false;
    },
    async processFile() {
      try {
        this.isProcessing = true;
        await axios.get(`crud_users/process_user`);
        this.is_Processed = true;
        await axios.get("crud_users/truncate_users");
        this.crud_users = [];
      } catch (error) {
        this.isError = true;
        this.error_msg = "Error processing file. Please try again.";
        console.error("Process error:", error);
      }
      this.isProcessing = false;
    },
    async truncatePendingDocuments() {
      try {
        await axios.get("crud_users/truncate_users");
        this.is_Truncated = true;
        this.crud_users = [];
      } catch (error) {
        console.error("Delete error:", error);
      }
    },
  },
};
</script>
<style scoped>
.users-upload {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #667eea !important;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

.back-btn {
  background: rgba(102, 126, 234, 0.1) !important;
  border: 1px solid rgba(102, 126, 234, 0.2) !important;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(102, 126, 234, 0.15) !important;
  transform: translateX(-2px);
}

/* Upload Card */
.upload-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.users-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* File Upload Area */
.upload-area {
  text-align: center;
  padding: 32px;
}

.file-upload-container {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 400px;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.file-upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 2px dashed rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  background: rgba(102, 126, 234, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-upload-label:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  transform: translateY(-2px);
}

.upload-icon {
  color: #667eea !important;
  margin-bottom: 16px;
}

.upload-text h3 {
  color: #2c3e50;
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
}

.upload-text p {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

/* Upload Actions */
.upload-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  background: rgba(34, 197, 94, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.actions-info {
  display: flex;
  align-items: center;
}

.actions-info h3 {
  color: #2c3e50;
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
}

.actions-info p {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

/* Filters Header Container */
.filters-header-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.count-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* Data Table */
.modern-data-table {
  background: transparent !important;
}

.modern-data-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-data-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-data-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-data-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-data-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-data-table >>> tbody tr td:last-child {
  border-right: none !important;
}

/* Serial Number Styling */
.serial-number {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

/* User Info Styling */
.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2px;
  font-size: 14px;
}

.contact-info {
  display: flex;
  flex-direction: column;
}

.contact-email {
  color: #2c3e50;
  margin-bottom: 2px;
  font-size: 14px;
  font-weight: 500;
}

.contact-phone {
  font-size: 12px;
  color: #64748b;
}

/* Modern Button Styling */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

.modern-btn:before {
  background-color: transparent;
}

/* Responsive */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
  
  .upload-actions {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    text-align: center;
    font-size: 14px;
  }
  
  .modern-card-title {
    padding: 16px !important;
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
  
  .filters-single-row {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .table-title-section {
    justify-content: center;
  }
  
  .upload-area {
    padding: 20px;
  }
  
  .file-upload-label {
    padding: 30px 15px;
  }
}
</style>
